import { apiFetchJson } from '@wundo/client/api.ts';
import type { SkillRecommendationStats } from '@wundo/shared/skillRecommendationStats.ts';
import type { PaginatedResponse } from '@/types/pagination.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';

export type { SkillRecommendationStats };

export type SkillRecommendationStatus =
  | 'pending'
  | 'delivered'
  | 'skipped'
  | 'superseded';

/** The one skill the batch engine picked for a user, and what became of it. */
export interface RecommendationRunSummary {
  id: string;
  run_id: string;
  user_id: string;
  user_name: string | null;
  skill_id: string;
  skill_name: string;
  recommended_at: string;
  final_score: number;
  status: SkillRecommendationStatus;
  reason: string;
  skipped_reason: string | null;
}

/** One candidate the LLM scored in a run — the winner included. */
export interface RecommendationEvaluatedSkill {
  skill_id: string;
  skill_name: string;
  llm_score: number;
  boost: number;
  freshness_factor: number;
  days_since_last_used: number | null;
  final_score: number;
  reason: string;
  was_selected: boolean;
}

export interface RecommendationRunDetail {
  winner: RecommendationRunSummary;
  evaluated: RecommendationEvaluatedSkill[];
}

/**
 * A candidate scored on the spot, outside any run. The shape differs from the
 * stored one: the preview carries the whole skill, run history carries its id.
 */
export interface SkillRecommendationPreview {
  skill: {
    id: string;
    name: string;
    description: string;
    boost: number;
    freshness: number;
  };
  llm_score: number;
  boost: number;
  freshness_factor: number;
  days_since_last_used: number | null;
  final_score: number;
  reason: string;
}

export function fetchRecommendationRuns(
  page = 1,
  limit: number = PAGE_SIZE,
): Promise<PaginatedResponse<RecommendationRunSummary>> {
  return apiFetchJson<PaginatedResponse<RecommendationRunSummary>>(
    `/skill-recommendations/runs?page=${page}&limit=${limit}`,
  );
}

export function fetchSkillRecommendationStats(
  windowDays = 7,
): Promise<SkillRecommendationStats> {
  return apiFetchJson<SkillRecommendationStats>(
    `/skill-recommendations/stats?window_days=${windowDays}`,
  );
}

export function fetchRecommendationRunDetail(
  runId: string,
  userId: string,
): Promise<RecommendationRunDetail> {
  return apiFetchJson<RecommendationRunDetail>(
    `/skill-recommendations/runs/${runId}/users/${userId}`,
  );
}

/** Scores every skill for one user now, without writing an outreach. */
export function previewSkillRecommendations(
  userId: string,
): Promise<SkillRecommendationPreview[]> {
  return apiFetchJson<SkillRecommendationPreview[]>(
    `/skill-recommendations/actions/preview?user_id=${userId}`,
  );
}
